/**
 * Seasonal Produce Agent
 * Provides information about seasonal produce availability in Southwest Florida
 */

interface SeasonalProduce {
  name: string;
  category: 'fruit' | 'vegetable' | 'herb' | 'other';
  peakMonths: number[];
  availableMonths: number[];
  description: string;
  nutritionHighlights: string[];
  storageTime: string;
  preparationTips: string[];
}

interface SeasonalCalendar {
  [key: string]: SeasonalProduce;
}

class SeasonalProduceAgent {
  private seasonalCalendar: SeasonalCalendar;

  constructor() {
    this.seasonalCalendar = this.initializeSeasonalCalendar();
  }

  /**
   * Initialize seasonal calendar for Southwest Florida
   */
  private initializeSeasonalCalendar(): SeasonalCalendar {
    return {
      tomatoes: {
        name: 'Tomatoes',
        category: 'vegetable',
        peakMonths: [1, 2, 3, 4, 11, 12],
        availableMonths: [1, 2, 3, 4, 10, 11, 12],
        description: 'Fresh, locally grown tomatoes',
        nutritionHighlights: ['Vitamin C', 'Lycopene', 'Potassium'],
        storageTime: '1-2 weeks at room temperature',
        preparationTips: ['Great for salads', 'Perfect for sauces', 'Roast for deeper flavor'],
      },
      strawberries: {
        name: 'Strawberries',
        category: 'fruit',
        peakMonths: [1, 2, 3, 4],
        availableMonths: [12, 1, 2, 3, 4],
        description: 'Sweet Florida strawberries',
        nutritionHighlights: ['Vitamin C', 'Manganese', 'Folate', 'Antioxidants'],
        storageTime: '3-7 days refrigerated',
        preparationTips: ['Eat fresh', 'Add to smoothies', 'Make preserves'],
      },
      avocados: {
        name: 'Avocados',
        category: 'fruit',
        peakMonths: [6, 7, 8, 9],
        availableMonths: [5, 6, 7, 8, 9, 10],
        description: 'Florida avocados',
        nutritionHighlights: ['Healthy fats', 'Fiber', 'Potassium', 'Vitamins'],
        storageTime: '3-5 days when ripe',
        preparationTips: ['Perfect for guacamole', 'Add to salads', 'Spread on toast'],
      },
      peppers: {
        name: 'Bell Peppers',
        category: 'vegetable',
        peakMonths: [1, 2, 3, 4, 10, 11, 12],
        availableMonths: [1, 2, 3, 4, 10, 11, 12],
        description: 'Colorful sweet peppers',
        nutritionHighlights: ['Vitamin C', 'Vitamin A', 'Fiber'],
        storageTime: '1-2 weeks refrigerated',
        preparationTips: ['Grill or roast', 'Add to stir-fries', 'Stuff and bake'],
      },
      lettuce: {
        name: 'Lettuce',
        category: 'vegetable',
        peakMonths: [1, 2, 3, 11, 12],
        availableMonths: [1, 2, 3, 10, 11, 12],
        description: 'Fresh salad greens',
        nutritionHighlights: ['Vitamin K', 'Vitamin A', 'Folate'],
        storageTime: '3-7 days refrigerated',
        preparationTips: ['Use in salads', 'Add to sandwiches', 'Make lettuce wraps'],
      },
      cucumbers: {
        name: 'Cucumbers',
        category: 'vegetable',
        peakMonths: [3, 4, 5, 10, 11],
        availableMonths: [3, 4, 5, 9, 10, 11],
        description: 'Crisp and refreshing',
        nutritionHighlights: ['Hydration', 'Vitamin K', 'Low calorie'],
        storageTime: '1 week refrigerated',
        preparationTips: ['Eat fresh in salads', 'Make pickles', 'Add to water'],
      },
      squash: {
        name: 'Summer Squash',
        category: 'vegetable',
        peakMonths: [3, 4, 5, 6],
        availableMonths: [3, 4, 5, 6, 7],
        description: 'Zucchini and yellow squash',
        nutritionHighlights: ['Vitamin C', 'Manganese', 'Fiber'],
        storageTime: '4-5 days refrigerated',
        preparationTips: ['Grill or sauté', 'Make zoodles', 'Add to casseroles'],
      },
      herbs: {
        name: 'Fresh Herbs',
        category: 'herb',
        peakMonths: [1, 2, 3, 4, 10, 11, 12],
        availableMonths: [1, 2, 3, 4, 5, 9, 10, 11, 12],
        description: 'Basil, cilantro, parsley, and more',
        nutritionHighlights: ['Antioxidants', 'Vitamins', 'Minerals'],
        storageTime: '1 week refrigerated in water',
        preparationTips: ['Use fresh in dishes', 'Make pesto', 'Dry for later use'],
      },
      greens: {
        name: 'Leafy Greens',
        category: 'vegetable',
        peakMonths: [1, 2, 3, 11, 12],
        availableMonths: [1, 2, 3, 10, 11, 12],
        description: 'Kale, collards, spinach',
        nutritionHighlights: ['Iron', 'Calcium', 'Vitamins A, C, K'],
        storageTime: '3-5 days refrigerated',
        preparationTips: ['Sauté with garlic', 'Add to smoothies', 'Use in salads'],
      },
      citrus: {
        name: 'Citrus Fruits',
        category: 'fruit',
        peakMonths: [11, 12, 1, 2, 3],
        availableMonths: [10, 11, 12, 1, 2, 3, 4],
        description: 'Oranges, grapefruits, lemons',
        nutritionHighlights: ['Vitamin C', 'Fiber', 'Antioxidants'],
        storageTime: '1-2 weeks at room temperature',
        preparationTips: ['Juice fresh', 'Eat as snack', 'Use in cooking'],
      },
    };
  }

  /**
   * Get produce that's in season for a given month
   */
  getProduceByMonth(month: number): SeasonalProduce[] {
    return Object.values(this.seasonalCalendar).filter((produce) =>
      produce.availableMonths.includes(month)
    );
  }

  /**
   * Get produce at peak season for a given month
   */
  getPeakProduceByMonth(month: number): SeasonalProduce[] {
    return Object.values(this.seasonalCalendar).filter((produce) =>
      produce.peakMonths.includes(month)
    );
  }

  /**
   * Get current season's produce
   */
  getCurrentSeasonProduce(): SeasonalProduce[] {
    const currentMonth = new Date().getMonth() + 1; // JavaScript months are 0-indexed
    return this.getProduceByMonth(currentMonth);
  }

  /**
   * Get current peak produce
   */
  getCurrentPeakProduce(): SeasonalProduce[] {
    const currentMonth = new Date().getMonth() + 1;
    return this.getPeakProduceByMonth(currentMonth);
  }

  /**
   * Get produce by category
   */
  getProduceByCategory(
    category: 'fruit' | 'vegetable' | 'herb' | 'other'
  ): SeasonalProduce[] {
    return Object.values(this.seasonalCalendar).filter(
      (produce) => produce.category === category
    );
  }

  /**
   * Search produce by name
   */
  searchProduce(query: string): SeasonalProduce[] {
    const lowerQuery = query.toLowerCase();
    return Object.values(this.seasonalCalendar).filter((produce) =>
      produce.name.toLowerCase().includes(lowerQuery)
    );
  }

  /**
   * Get seasonal recommendations
   */
  getSeasonalRecommendations(month?: number): {
    month: number;
    monthName: string;
    peakProduce: SeasonalProduce[];
    available: SeasonalProduce[];
    tips: string[];
  } {
    const targetMonth = month || new Date().getMonth() + 1;
    const monthNames = [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ];

    const peakProduce = this.getPeakProduceByMonth(targetMonth);
    const available = this.getProduceByMonth(targetMonth);

    const tips = this.getSeasonalTips(targetMonth);

    return {
      month: targetMonth,
      monthName: monthNames[targetMonth - 1],
      peakProduce,
      available,
      tips,
    };
  }

  /**
   * Get seasonal tips based on month
   */
  private getSeasonalTips(month: number): string[] {
    const winterMonths = [12, 1, 2];
    const springMonths = [3, 4, 5];
    const summerMonths = [6, 7, 8];
    const fallMonths = [9, 10, 11];

    if (winterMonths.includes(month)) {
      return [
        'Peak season for citrus fruits',
        'Great time for leafy greens',
        'Strawberry season begins',
        'Perfect weather for cool-season crops',
      ];
    } else if (springMonths.includes(month)) {
      return [
        'Transition to warm-season crops',
        'Last chance for some winter vegetables',
        'Tomatoes start coming in',
        'Fresh herbs are abundant',
      ];
    } else if (summerMonths.includes(month)) {
      return [
        'Hot weather limits some crops',
        'Great for tropical fruits',
        'Stay hydrated with fresh produce',
        'Focus on heat-tolerant varieties',
      ];
    } else {
      return [
        'Fall planting season begins',
        'Cool-season crops return',
        'Great time for peppers',
        'Transition back to winter favorites',
      ];
    }
  }

  /**
   * Get yearly calendar view
   */
  getYearlyCalendar(): { [month: number]: SeasonalProduce[] } {
    const calendar: { [month: number]: SeasonalProduce[] } = {};

    for (let month = 1; month <= 12; month++) {
      calendar[month] = this.getPeakProduceByMonth(month);
    }

    return calendar;
  }

  /**
   * Check if produce is in season
   */
  isInSeason(produceName: string, month?: number): boolean {
    const targetMonth = month || new Date().getMonth() + 1;
    const produce = Object.values(this.seasonalCalendar).find(
      (p) => p.name.toLowerCase() === produceName.toLowerCase()
    );

    return produce ? produce.availableMonths.includes(targetMonth) : false;
  }
}

export default new SeasonalProduceAgent();
